// Catches lazy-route (code-split chunk) load failures, which otherwise render
// a blank screen or a raw "load failed" — typically on ship/PPU wifi when a
// screen wasn't cached before the connection died. Gives a plain-language
// message and real recovery instead of stranding the pilot.
//
// 2026-07-24 (Jill's iPad mini brick): "Go Back" used history.back(), which
// no-ops in a homescreen PWA launched fresh (no history entry) — so both
// buttons appeared dead. Recovery now sends the pilot HOME ("/"), which is the
// shell route and almost always cached, and the boundary tracks connectivity
// so "Try Again" is only offered as the primary action when it can actually
// succeed (online). Offline, going home is the way out.
import React from "react";
import { computeSyncSummary } from "../lib/syncSummary";
import { supabase } from "../supabaseClient";

export default class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, online: navigator.onLine };
    this._onOnline = () => this.setState({ online: true });
    this._onOffline = () => this.setState({ online: false });
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidMount() {
    window.addEventListener("online", this._onOnline);
    window.addEventListener("offline", this._onOffline);
  }
  componentWillUnmount() {
    window.removeEventListener("online", this._onOnline);
    window.removeEventListener("offline", this._onOffline);
  }
  componentDidCatch(error, info) {
    console.error("Route load failed:", error);
    // Remote forensics (2026-07-24): an iPad showing this screen has no
    // reachable console, so the failure was invisible — report the REAL
    // error to the debug log where it can be read server-side. Strictly
    // fire-and-forget: reporting must never affect recovery.
    try {
      const msg = [
        String(error?.message || error).slice(0, 400),
        String(info?.componentStack || "").replace(/\s+/g, " ").slice(0, 300),
        navigator.userAgent,
        `path=${window.location.pathname}`,
      ].join(" | ");
      supabase.from("seapa_debug_log")
        .insert({ step: "route_error", msg })
        .then(() => {}, () => {});
    } catch { /* reporting is best-effort */ }
  }
  goHome = () => {
    this.setState({ error: null });
    // assign("/") beats history.back(): it always resolves to the shell route
    // (cached via navigateFallback) and works even when the PWA was launched
    // straight into the broken screen with no back-stack.
    window.location.assign("/");
  };
  tryAgain = () => {
    this.setState({ error: null });
    window.location.reload();
  };
  render() {
    if (!this.state.error) return this.props.children;
    const offlineish =
      !this.state.online ||
      /fetch|import|load/i.test(String(this.state.error?.message || ""));

    // This screen is where "just clear the app's data / reinstall" decisions
    // get made — by the pilot or whoever is helping them. If anything is
    // saved ONLY on this device (queued submits, receipts waiting to upload,
    // unsaved slip edits), say so HERE, before it can be destroyed
    // (2026-07-24: a cache-clear recovery step cost a receipt exactly this way).
    let deviceOnly = 0;
    try { deviceOnly = computeSyncSummary().waiting; } catch { /* storage unavailable */ }

    const primaryBtn = {
      background: "var(--btn-primary-bg)", color: "var(--btn-primary-fg)",
      border: "none", borderRadius: 8, padding: "10px 18px", fontSize: "1rem",
      fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
    };
    const secondaryBtn = {
      background: "var(--surface)", color: "var(--primary)",
      border: "1px solid var(--border)", borderRadius: 8, padding: "10px 18px",
      fontSize: "1rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
      marginLeft: 8,
    };

    return (
      <main style={{ padding: 24, maxWidth: 560, margin: "40px auto", textAlign: "left", fontFamily: "inherit" }}>
        <h2 style={{ color: "var(--primary)", marginTop: 0 }}>This screen didn't load</h2>
        <p style={{ color: "var(--ink)", lineHeight: 1.6 }}>
          {offlineish
            ? "It looks like this device doesn't have a working internet connection right now (ship or PPU wifi often shows connected without internet). Screens you've opened before keep working offline. Tap Go to Home to get back to the parts that work; this screen needs one more visit while connected."
            : "Something went wrong loading this screen. Go to Home to get back, or Try Again."}
        </p>
        {deviceOnly > 0 && (
          <p style={{
            color: "var(--warn-fg)", background: "var(--warn-bg, rgba(180,120,0,0.08))",
            border: "1px solid var(--warn-fg)", borderRadius: 8, padding: "10px 14px",
            lineHeight: 1.5, fontWeight: 600,
          }}>
            {deviceOnly} item{deviceOnly > 1 ? "s" : ""} (receipts, drafts, or queued
            submissions) {deviceOnly > 1 ? "are" : "is"} saved only on this device and
            {deviceOnly > 1 ? " haven't" : " hasn't"} reached the server yet. Do NOT
            clear this app's website data or delete the app, because that would permanently
            destroy {deviceOnly > 1 ? "them" : "it"}. Everything uploads by itself the
            next time the app opens with a working connection.
          </p>
        )}
        {/* Offline: Home is the reliable way out, so it leads. Online: a reload
            usually fixes a half-loaded chunk, so Try Again leads. */}
        {offlineish ? (
          <>
            <button onClick={this.goHome} style={primaryBtn}>Go to Home</button>
            <button onClick={this.tryAgain} style={secondaryBtn}>Try Again</button>
          </>
        ) : (
          <>
            <button onClick={this.tryAgain} style={primaryBtn}>Try Again</button>
            <button onClick={this.goHome} style={secondaryBtn}>Go to Home</button>
          </>
        )}
      </main>
    );
  }
}
